import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Users,
  UserPlus,
  Mail,
  Trash2,
  Crown,
  Shield,
  Eye,
  Loader2,
  Send,
  Clock,
  LogOut,
  ArrowLeftRight,
} from 'lucide-react';
import { UserLayout } from '../components/User/UserLayout';
import { useAuthStore } from '../store/authStore';
import { useAccountStore, useEffectivePlan } from '../store/accountStore';
import { getMaxTeamMembers } from '../constants/planLimits';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

type Role = 'editor' | 'viewer';

interface Member {
  id: string;
  userId: string;
  role: Role;
  status: string;
  name: string;
  email: string | null;
  avatar: string | null;
  createdAt: string;
}

interface PendingInvite {
  id: string;
  email: string;
  role: Role;
  createdAt: string;
  expiresAt: string | null;
}

interface Seats {
  used: number;
  max: number; // -1 = unlimited
  remaining: number; // -1 = unlimited
  plan: string;
}

interface IncomingInvite {
  id: string;
  token: string;
  role: Role;
  createdAt: string;
  expiresAt: string | null;
  owner: { id: string; name: string; email: string | null };
}

function authHeaders(json = false): HeadersInit {
  const token = localStorage.getItem('token');
  const h: Record<string, string> = {};
  if (token) h.Authorization = `Bearer ${token}`;
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

const RoleBadge: React.FC<{ role: Role }> = ({ role }) => {
  const { t } = useTranslation('common');
  if (role === 'viewer') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
        <Eye className="w-3 h-3" />
        {t('team.viewer', { defaultValue: 'Viewer' })}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
      <Shield className="w-3 h-3" />
      {t('team.editor', { defaultValue: 'Editor' })}
    </span>
  );
};

export const TeamSettings: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const accounts = useAccountStore((s) => s.accounts);
  const activeAccountId = useAccountStore((s) => s.activeAccountId);
  const setActiveAccount = useAccountStore((s) => s.setActiveAccount);
  const loadAccounts = useAccountStore((s) => s.loadAccounts);
  const effectivePlan = useEffectivePlan();

  const selfId = user?.id || null;
  const effectiveAccountId = activeAccountId || selfId;
  const onOwnAccount = !effectiveAccountId || effectiveAccountId === selfId;

  const planSeats = getMaxTeamMembers(effectivePlan);
  const teamEnabled = planSeats === -1 || planSeats > 0;

  const [members, setMembers] = useState<Member[]>([]);
  const [pending, setPending] = useState<PendingInvite[]>([]);
  const [seats, setSeats] = useState<Seats | null>(null);
  const [incoming, setIncoming] = useState<IncomingInvite[]>([]);
  const [loading, setLoading] = useState(true);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('editor');
  const [inviting, setInviting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const memberships = accounts.filter((a) => !a.isSelf);

  const fetchMembers = useCallback(async () => {
    if (!onOwnAccount || !teamEnabled) return;
    try {
      const res = await fetch(`${API_URL}/team/members`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setMembers(data.members || []);
      setPending(data.invitations || []);
      setSeats(data.seats || null);
    } catch (err) {
      console.error('[Team] fetchMembers failed:', err);
    }
  }, [onOwnAccount, teamEnabled]);

  const fetchIncoming = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/team/invitations/incoming`, {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      setIncoming(data.invitations || []);
    } catch (err) {
      console.error('[Team] fetchIncoming failed:', err);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchMembers(), fetchIncoming()]).finally(() => setLoading(false));
  }, [fetchMembers, fetchIncoming]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email) return;
    setInviting(true);
    try {
      const res = await fetch(`${API_URL}/team/invite`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ email, role: inviteRole }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Plan-gate 403s are handled globally by the interceptor (upgrade
        // modal). For other errors show the server message.
        if (res.status !== 403) {
          toast.error(data.message || data.error || t('team.inviteFailed', { defaultValue: 'Failed to send invitation' }));
        }
        return;
      }
      toast.success(t('team.inviteSent', { defaultValue: 'Invitation sent' }));
      setInviteEmail('');
      setInviteRole('editor');
      await fetchMembers();
    } catch (err) {
      console.error('[Team] invite failed:', err);
      toast.error(t('team.inviteFailed', { defaultValue: 'Failed to send invitation' }));
    } finally {
      setInviting(false);
    }
  };

  const handleResend = async (id: string) => {
    setBusyId(id);
    try {
      const res = await fetch(`${API_URL}/team/invitations/${id}/resend`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (res.ok) toast.success(t('team.inviteResent', { defaultValue: 'Invitation re-sent' }));
      else toast.error(t('common.error', { defaultValue: 'Something went wrong' }));
    } finally {
      setBusyId(null);
    }
  };

  const handleRevoke = async (id: string) => {
    setBusyId(id);
    try {
      const res = await fetch(`${API_URL}/team/invitations/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.ok) {
        toast.success(t('team.inviteRevoked', { defaultValue: 'Invitation revoked' }));
        await fetchMembers();
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleChangeRole = async (member: Member, role: Role) => {
    setBusyId(member.id);
    try {
      const res = await fetch(`${API_URL}/team/members/${member.id}`, {
        method: 'PATCH',
        headers: authHeaders(true),
        body: JSON.stringify({ role }),
      });
      if (res.ok) {
        toast.success(t('team.roleUpdated', { defaultValue: 'Role updated' }));
        await fetchMembers();
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (member: Member) => {
    if (!window.confirm(t('team.confirmRemove', { defaultValue: 'Remove this member from your workspace?' }))) {
      return;
    }
    setBusyId(member.id);
    try {
      const res = await fetch(`${API_URL}/team/members/${member.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.ok) {
        toast.success(t('team.memberRemoved', { defaultValue: 'Member removed' }));
        await fetchMembers();
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleAccept = async (invite: IncomingInvite) => {
    setBusyId(invite.id);
    try {
      const res = await fetch(`${API_URL}/team/invitations/accept`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ token: invite.token }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.message || data.error || t('team.acceptFailed', { defaultValue: 'Failed to accept invitation' }));
        return;
      }
      toast.success(t('team.joined', { defaultValue: 'You joined the workspace' }));
      await loadAccounts();
      await fetchIncoming();
    } finally {
      setBusyId(null);
    }
  };

  const handleLeave = async (ownerId: string) => {
    if (!window.confirm(t('team.confirmLeave', { defaultValue: 'Leave this workspace?' }))) return;
    setBusyId(ownerId);
    try {
      const res = await fetch(`${API_URL}/team/leave`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ ownerId }),
      });
      if (res.ok) {
        toast.success(t('team.left', { defaultValue: 'You left the workspace' }));
        if (effectiveAccountId === ownerId && selfId) setActiveAccount(selfId);
        await loadAccounts();
      }
    } finally {
      setBusyId(null);
    }
  };

  const seatLabel = seats
    ? seats.max === -1
      ? t('team.seatsUnlimited', { defaultValue: '{{used}} used · Unlimited seats', used: seats.used })
      : t('team.seatsUsage', { defaultValue: '{{used}} of {{max}} seats used', used: seats.used, max: seats.max })
    : '';

  const seatFull = seats ? seats.max !== -1 && seats.remaining <= 0 : false;

  return (
    <UserLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('team.title', { defaultValue: 'Team' })}
            </h1>
            <p className="text-sm text-gray-500">
              {t('team.subtitle', { defaultValue: 'Invite people to help build and manage your landing pages.' })}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <div className="space-y-6 mt-6">
            {/* Incoming invitations addressed to me */}
            {incoming.length > 0 && (
              <section className="bg-white rounded-2xl border border-blue-200 shadow-sm overflow-hidden">
                <div className="px-5 py-4 bg-blue-50 border-b border-blue-100">
                  <h2 className="font-semibold text-blue-900 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {t('team.incomingTitle', { defaultValue: 'Invitations to you' })}
                  </h2>
                </div>
                <ul className="divide-y divide-gray-100">
                  {incoming.map((inv) => (
                    <li key={inv.id} className="px-5 py-4 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {inv.owner.name}
                          <span className="text-gray-400 font-normal"> · {inv.owner.email}</span>
                        </p>
                        <div className="mt-1">
                          <RoleBadge role={inv.role} />
                        </div>
                      </div>
                      <button
                        onClick={() => handleAccept(inv)}
                        disabled={busyId === inv.id}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 shrink-0"
                      >
                        {busyId === inv.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          t('team.accept', { defaultValue: 'Accept' })
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Workspaces I'm a member of */}
            {memberships.length > 0 && (
              <section className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100">
                  <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                    <ArrowLeftRight className="w-4 h-4 text-gray-500" />
                    {t('team.memberOfTitle', { defaultValue: "Workspaces you're a member of" })}
                  </h2>
                </div>
                <ul className="divide-y divide-gray-100">
                  {memberships.map((m) => (
                    <li key={m.id} className="px-5 py-4 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{m.name}</p>
                        <div className="mt-1 flex items-center gap-2">
                          <RoleBadge role={m.role as Role} />
                          {effectiveAccountId === m.id && (
                            <span className="text-xs text-green-600 font-medium">
                              {t('team.current', { defaultValue: 'Current' })}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {effectiveAccountId !== m.id && (
                          <button
                            onClick={() => {
                              setActiveAccount(m.id);
                              window.location.assign('/dashboard');
                            }}
                            className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg font-medium"
                          >
                            {t('team.switch', { defaultValue: 'Switch' })}
                          </button>
                        )}
                        <button
                          onClick={() => handleLeave(m.id)}
                          disabled={busyId === m.id}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                          title={t('team.leave', { defaultValue: 'Leave' })}
                        >
                          <LogOut className="w-4 h-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Management section — only when acting on your own account */}
            {!onOwnAccount ? (
              <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 text-center">
                <Users className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 mb-4">
                  {t('team.switchToManage', {
                    defaultValue: 'Switch back to your own account to manage your team.',
                  })}
                </p>
                {selfId && (
                  <button
                    onClick={() => {
                      setActiveAccount(selfId);
                      window.location.assign('/team');
                    }}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
                  >
                    {t('team.backToOwn', { defaultValue: 'Back to my account' })}
                  </button>
                )}
              </section>
            ) : !teamEnabled ? (
              <section className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-6 text-center">
                <Crown className="w-8 h-8 text-amber-500 mx-auto mb-3" />
                <h2 className="font-semibold text-gray-900 mb-1">
                  {t('team.upgradeTitle', { defaultValue: 'Team collaboration is a paid feature' })}
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  {t('team.upgradeBody', {
                    defaultValue: 'Invite editors and viewers on the Pro, Agency, and Enterprise plans.',
                  })}
                </p>
                <button
                  onClick={() => navigate('/upgrade')}
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium"
                >
                  {t('nav.upgradePlan', { defaultValue: 'Upgrade plan' })}
                </button>
              </section>
            ) : (
              <>
                {/* Invite form */}
                <section className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
                    <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                      <UserPlus className="w-4 h-4 text-gray-500" />
                      {t('team.inviteTitle', { defaultValue: 'Invite a member' })}
                    </h2>
                    {seats && (
                      <span className={`text-xs font-medium ${seatFull ? 'text-red-600' : 'text-gray-500'}`}>
                        {seatLabel}
                      </span>
                    )}
                  </div>
                  <form onSubmit={handleInvite} className="p-5 flex flex-col sm:flex-row gap-3">
                    <input
                      type="email"
                      required
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder={t('team.emailPlaceholder', { defaultValue: 'colleague@email.com' })}
                      className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value as Role)}
                      className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                    >
                      <option value="editor">{t('team.editor', { defaultValue: 'Editor' })}</option>
                      <option value="viewer">{t('team.viewer', { defaultValue: 'Viewer' })}</option>
                    </select>
                    <button
                      type="submit"
                      disabled={inviting || seatFull}
                      className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      {t('team.sendInvite', { defaultValue: 'Send invite' })}
                    </button>
                  </form>
                  {seatFull && (
                    <p className="px-5 pb-4 -mt-2 text-xs text-red-600">
                      {t('team.seatFull', {
                        defaultValue: 'You have used all your seats. Remove a member or upgrade to invite more.',
                      })}
                    </p>
                  )}
                </section>

                {/* Pending invitations */}
                {pending.length > 0 && (
                  <section className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="px-5 py-4 border-b border-gray-100">
                      <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        {t('team.pendingTitle', { defaultValue: 'Pending invitations' })}
                      </h2>
                    </div>
                    <ul className="divide-y divide-gray-100">
                      {pending.map((inv) => (
                        <li key={inv.id} className="px-5 py-4 flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{inv.email}</p>
                            <div className="mt-1">
                              <RoleBadge role={inv.role} />
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              onClick={() => handleResend(inv.id)}
                              disabled={busyId === inv.id}
                              className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg font-medium disabled:opacity-50"
                            >
                              {t('team.resend', { defaultValue: 'Resend' })}
                            </button>
                            <button
                              onClick={() => handleRevoke(inv.id)}
                              disabled={busyId === inv.id}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                              title={t('team.revoke', { defaultValue: 'Revoke' })}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {/* Active members */}
                <section className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100">
                    <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-500" />
                      {t('team.membersTitle', { defaultValue: 'Members' })}
                    </h2>
                  </div>
                  {members.length === 0 ? (
                    <div className="px-5 py-10 text-center text-sm text-gray-400">
                      {t('team.noMembers', { defaultValue: 'No members yet. Invite someone above.' })}
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {members.map((m) => (
                        <li key={m.id} className="px-5 py-4 flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-sm font-semibold shrink-0 overflow-hidden">
                              {m.avatar ? (
                                <img src={m.avatar} alt={m.name} className="w-full h-full object-cover" />
                              ) : (
                                (m.name || m.email || '?').charAt(0).toUpperCase()
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{m.name}</p>
                              <p className="text-xs text-gray-500 truncate">{m.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <select
                              value={m.role}
                              onChange={(e) => handleChangeRole(m, e.target.value as Role)}
                              disabled={busyId === m.id}
                              className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white disabled:opacity-50"
                            >
                              <option value="editor">{t('team.editor', { defaultValue: 'Editor' })}</option>
                              <option value="viewer">{t('team.viewer', { defaultValue: 'Viewer' })}</option>
                            </select>
                            <button
                              onClick={() => handleRemove(m)}
                              disabled={busyId === m.id}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                              title={t('team.remove', { defaultValue: 'Remove' })}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </>
            )}
          </div>
        )}
      </div>
    </UserLayout>
  );
};

export default TeamSettings;
